/**
 * Canonical base URL for links we hand to users to distribute.
 *
 * Workplace post 1591255772631720. The API returns share paths as RELATIVE
 * URLs (`/published/{token}/{slug}` — see `routes/sharing.py`), and the console
 * turned them absolute with `window.location.origin`. That bakes whatever host
 * the publisher happened to be on into a link they then send to partners.
 *
 * A partner published while the console was served on
 * `console.prod-meta.dream-wright.com`; that host is now unreachable, so the
 * links they distributed are dead even though the underlying publication is
 * fine on `console.dreamwrite.com`.
 *
 * `window.location.origin` is the right default for same-origin navigation, but
 * the WRONG default for a URL whose whole purpose is to outlive the session and
 * be opened by someone else. Deployments that serve the console on more than
 * one hostname (legacy aliases, per-environment hosts) set
 * `VITE_PUBLIC_SHARE_BASE_URL` to the one canonical public origin.
 *
 * This cannot repair links already distributed — only a redirect from the dead
 * host can do that. It stops new ones being minted against a non-canonical host.
 */

import { createNadDebug } from './debugLog'

// Diagnosing this class of bug from a screenshot is painful — the only clue is
// the host inside a URL someone pasted elsewhere. Logging which source won and
// what host resulted makes it answerable from a trace.
const shareLinkLog = createNadDebug('dw:published-share-link')

/** Strip trailing slashes so joining with a leading-slash path never doubles up. */
function normalizeBase(base: string): string {
  return base.replace(/\/+$/, '')
}

/**
 * The origin that public, shareable links should be built from.
 *
 * Falls back to `window.location.origin` when unconfigured, which preserves
 * today's behaviour for local dev and single-host deployments.
 */
export function getPublicShareBaseUrl(): string {
  const configured = import.meta.env.VITE_PUBLIC_SHARE_BASE_URL
  if (typeof configured === 'string' && configured.trim() !== '') {
    return normalizeBase(assertValidOrigin(configured.trim()))
  }
  return typeof window !== 'undefined' ? normalizeBase(window.location.origin) : ''
}

/**
 * A configured base must be a bare http(s) ORIGIN.
 *
 * `VITE_PUBLIC_SHARE_BASE_URL=console.dreamwrite.com` (no scheme) would
 * otherwise yield `console.dreamwrite.com/published/...` — a relative-looking
 * string that silently produces exactly the class of broken link this module
 * exists to prevent. A path/query/fragment is equally wrong: the share path is
 * appended verbatim, so anything beyond the origin corrupts the result.
 *
 * Fails loudly rather than degrading (CLAUDE.md #2). A misconfigured deployment
 * should be impossible to miss, not quietly emit unusable links to partners.
 */
function assertValidOrigin(value: string): string {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    throw new Error(
      `VITE_PUBLIC_SHARE_BASE_URL must be an absolute http(s) origin, got: ${value}`,
    )
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(
      `VITE_PUBLIC_SHARE_BASE_URL must use http or https, got: ${parsed.protocol}`,
    )
  }
  if ((parsed.pathname !== '/' && parsed.pathname !== '') || parsed.search || parsed.hash) {
    throw new Error(
      `VITE_PUBLIC_SHARE_BASE_URL must be a bare origin with no path, query or fragment, got: ${value}`,
    )
  }
  return parsed.origin
}

/**
 * Build an absolute, distributable URL from an API-returned relative share path.
 *
 * An absolute `sharePath` is returned unchanged: the backend is entitled to
 * hand back a fully-qualified URL, and rewriting its host here would be a
 * silent override rather than a default.
 */
export function buildPublicShareUrl(sharePath: string): string {
  if (/^https?:\/\//i.test(sharePath)) {
    shareLinkLog({ event: 'share-link:built', baseSource: 'absolute-path', host: null })
    return sharePath
  }
  const configured = import.meta.env.VITE_PUBLIC_SHARE_BASE_URL
  const usedConfigured = typeof configured === 'string' && configured.trim() !== ''
  const base = getPublicShareBaseUrl()
  const path = sharePath.startsWith('/') ? sharePath : `/${sharePath}`
  const url = `${base}${path}`

  let host: string | null = null
  try {
    host = new URL(url).host
  } catch {
    // A malformed base should surface in the trace, not throw at copy time.
    host = null
  }
  shareLinkLog({
    event: 'share-link:built',
    // 'origin' means this deployment has not set a canonical host, so the link
    // inherits whatever alias the publisher is on — the post-1591255772631720 trap.
    baseSource: usedConfigured ? 'configured' : 'origin',
    host,
  })
  return url
}
